import React, { useRef, useState } from 'react';

import { CloudUpload as CloudUploadOutlined } from "lucide-react";
import { Box, Button, Stack, TextField, Typography } from '@mui/material';

import FileService from '@/app/_services/FileService';
import LoadingSpinner from '@/app/admin/_common/LoadingSpinner';

const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/gif,image/webp';

type ImageSourceInputProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
};

export default function ImageSourceInput({
  label,
  value,
  onChange,
}: ImageSourceInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFileSelect(file: File | undefined) {
    if (!file) {
      return;
    }

    setError(null);
    setIsUploading(true);

    try {
      const dataUrl = await readFileAsDataUrl(file);
      const { url } = await FileService.uploadImage(dataUrl);
      onChange(url);
    } catch (uploadError) {
      const message =
        uploadError instanceof Error
          ? uploadError.message
          : 'Image upload failed';
      setError(message);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }

  return (
    <Stack spacing={1}>
      <TextField
        fullWidth
        variant="standard"
        label={label}
        value={value}
        disabled={isUploading}
        onChange={(event) => {
          setError(null);
          onChange(event.target.value);
        }}
      />

      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="outlined"
          size="small"
          disabled={isUploading}
          startIcon={
            isUploading ? (
              <LoadingSpinner showText={false} size={16} />
            ) : (
              <CloudUploadOutlined className="size-4" />
            )
          }
          onClick={() => fileInputRef.current?.click()}
          sx={{ textTransform: 'none' }}
        >
          {isUploading ? 'Uploading...' : 'Upload image'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          PNG, JPEG, GIF, or WebP
        </Typography>
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        hidden
        onChange={(event) => {
          void handleFileSelect(event.target.files?.[0]);
        }}
      />

      {value ? (
        <Box
          component="img"
          src={value}
          alt="Image preview"
          sx={{
            width: '100%',
            maxHeight: 120,
            objectFit: 'contain',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: '#f8fafc',
          }}
        />
      ) : null}

      {error ? (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      ) : null}
    </Stack>
  );
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('Failed to read image file'));
    };
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });
}
